/**
 * PostHog Context Provider
 * Initializes PostHog with self-hosted instance and provides analytics hooks
 */

import React, { createContext, useContext, useEffect, useRef, ReactNode, useCallback } from 'react';
import posthog from 'posthog-js';
import { useConfig } from './ConfigProvider';
import {
  createPostHogSanitizer,
  MASK_TEXT_SELECTOR
} from '../utils/analytics-masking';
import type { AnalyticsUserProperties, GroupType } from '../types/analytics';

/** PostHog context value type */
interface PostHogContextValue {
  /** Whether PostHog is initialized and ready */
  isReady: boolean;
  /** Whether PostHog is enabled via configuration */
  isEnabled: boolean;
  /** Identify a user with properties */
  identify: (userId: string, properties?: AnalyticsUserProperties) => void;
  /** Reset user identity (call on logout) */
  reset: () => void;
  /** Set a group for the current user (for Group Analytics) */
  setGroup: (groupType: GroupType, groupKey: string, properties?: Record<string, unknown>) => void;
}

const PostHogContext = createContext<PostHogContextValue | undefined>(undefined);

interface PostHogProviderProps {
  children: ReactNode;
}

/**
 * PostHog Provider Component
 * Initializes PostHog with fintech-compliant configuration
 */
export function PostHogProvider({ children }: PostHogProviderProps) {
  const config = useConfig();
  const initializedRef = useRef(false);
  const isReadyRef = useRef(false);

  const isEnabled =
    config.POSTHOG_ENABLED === 'true' &&
    Boolean(config.POSTHOG_API_KEY) &&
    Boolean(config.POSTHOG_HOST);

  // Initialize PostHog on mount
  useEffect(() => {
    if (initializedRef.current || !isEnabled) {
      return;
    }

    const apiKey = config.POSTHOG_API_KEY;
    const hostUrl = config.POSTHOG_HOST;

    if (!apiKey || !hostUrl) {
      console.warn('[PostHog] Missing API key or host URL, skipping initialization');
      return;
    }

    try {
      posthog.init(apiKey, {
        api_host: hostUrl,
        // Capture settings
        autocapture: {
          dom_event_allowlist: ['click', 'submit', 'change'],
          element_attribute_ignorelist: ['data-testid', 'data-cy']
        },
        capture_pageview: false, // We handle this manually via RouteTracker
        capture_pageleave: true,
        // Session recording with aggressive PII masking for fintech
        enable_recording_console_log: false, // Don't capture console logs (may contain sensitive data)
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: MASK_TEXT_SELECTOR,
          maskInputOptions: {
            password: true,
            email: true,
            tel: true,
            number: true, // Mask numeric inputs (amounts, account numbers)
            text: false // Allow text but specific selectors will mask
          }
        },
        // Privacy & compliance
        sanitize_properties: createPostHogSanitizer(),
        property_denylist: ['$ip'], // Don't send IP addresses
        respect_dnt: false, // Internal tool, don't respect DNT
        // Performance
        loaded: (ph) => {
          isReadyRef.current = true;
          // Register app-level super properties
          ph.register({
            app_name: 'utx-ops-web',
            app_env: config.APP_ENV,
            tenant: config.TENANT
          });
        },
        // Disable debug in production
        debug: config.APP_ENV === 'dev',
        // Persistence
        persistence: 'localStorage+cookie',
        // Bootstrap for faster feature flags
        bootstrap: {
          distinctID: undefined, // Will be set on identify
          featureFlags: {}
        }
      });

      initializedRef.current = true;
    } catch (error) {
      console.error('[PostHog] Failed to initialize:', error);
    }

    // Cleanup on unmount
    return () => {
      // PostHog doesn't have a destroy method, but we can opt out
      // Don't actually destroy - this would affect other tabs
    };
  }, [isEnabled, config]);

  // Identify user with properties
  const identify = useCallback(
    (userId: string, properties?: AnalyticsUserProperties) => {
      if (!isEnabled || !isReadyRef.current) {
        return;
      }

      try {
        posthog.identify(userId, properties);
      } catch (error) {
        console.warn('[PostHog] Failed to identify user:', error);
      }
    },
    [isEnabled]
  );

  // Reset user identity
  const reset = useCallback(() => {
    if (!isEnabled || !isReadyRef.current) {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      console.warn('[PostHog] Failed to reset user:', error);
    }
  }, [isEnabled]);

  // Set group for Group Analytics (e.g., vendor segmentation)
  const setGroup = useCallback(
    (groupType: GroupType, groupKey: string, properties?: Record<string, unknown>) => {
      if (!isEnabled || !isReadyRef.current) {
        return;
      }

      try {
        posthog.group(groupType, groupKey, properties);
      } catch (error) {
        console.warn('[PostHog] Failed to set group:', error);
      }
    },
    [isEnabled]
  );

  const contextValue: PostHogContextValue = {
    isReady: isReadyRef.current,
    isEnabled,
    identify,
    reset,
    setGroup
  };

  return (
    <PostHogContext.Provider value={contextValue}>
      {children}
    </PostHogContext.Provider>
  );
}

/**
 * Hook to access PostHog context
 * @returns PostHog context value
 * @throws Error if used outside PostHogProvider
 */
export function usePostHog(): PostHogContextValue {
  const context = useContext(PostHogContext);
  if (!context) {
    throw new Error('usePostHog must be used within a PostHogProvider');
  }
  return context;
}

/**
 * Hook to get PostHog context safely (returns null if not in provider)
 * Use this for components that may or may not be within PostHogProvider
 */
export function usePostHogSafe(): PostHogContextValue | null {
  return useContext(PostHogContext) ?? null;
}

export default PostHogProvider;
